import type { OpeningHoursSpecification } from 'next-seo';

import type { Country } from '@/types/regionTypes';

import {
  applyProductConfigurationFilters,
  checkProductActivation,
  getIndexName,
  ProductConfigurationFilterType,
} from '@/integrations/algolia/helpers';
import { createSearchClient } from '@/integrations/algolia/searchClientBuilder';
import type {
  EyewearHit,
  StrapHit,
  WatchHit,
} from '@/integrations/algolia/types';
import { LocaleConfiguration } from '@/lib/configuration/configuration';
import { isSupportedLocale } from '@/lib/locales/locales';

const makeHrefLangValues = (
  seoHrefLangConfig: {
    [key: string]: string[];
  },
  languageCode: string | undefined,
  supportedLanguages: string[],
  supportedCountryCodes: string[],
) => {
  const notNull = <T>(value: T | null): value is T => value !== null;
  return Object.entries(seoHrefLangConfig).flatMap(([language, countries]) => {
    if (
      (languageCode && language !== languageCode) ||
      !supportedLanguages?.includes(language)
    ) {
      return [];
    }

    return countries
      .map((country) => {
        if (!supportedCountryCodes?.includes(country)) {
          return null;
        }
        return `${language}-${country.toUpperCase()}`;
      })
      .filter(notNull);
  });
};

function excludeNonSupportedLocales(hrefLangValues: string[]) {
  return hrefLangValues.filter((altLanguage) => {
    const alternateLocale = altLanguage
      .toLowerCase()
      .split('-')
      .reverse()
      .join('-');
    return isSupportedLocale(alternateLocale);
  });
}

export const makeHrefLangs = (
  supportedCountries: Country[],
  seoHrefLangConfig: { [key: string]: string[] },
  languageCode?: string,
): string[] => {
  const supportedLanguages = [
    ...Array.from(
      new Set(
        supportedCountries
          .map((country) => country.locales.map((locale) => locale.slug))
          .flat(),
      ),
    ),
  ];

  const supportedCountryCodes = [
    ...Array.from(
      new Set(supportedCountries.map((country) => country.code.toLowerCase())),
    ),
  ];

  if (languageCode && ['zht', 'zh']?.includes(languageCode)) {
    return [
      ...makeHrefLangValues(
        seoHrefLangConfig,
        'zht',
        supportedLanguages,
        supportedCountryCodes,
      ),
      ...makeHrefLangValues(
        seoHrefLangConfig,
        'zh',
        supportedLanguages,
        supportedCountryCodes,
      ),
    ];
  }

  const hrefLangValues = makeHrefLangValues(
    seoHrefLangConfig,
    languageCode,
    supportedLanguages,
    supportedCountryCodes,
  );

  return excludeNonSupportedLocales(hrefLangValues);
};

/**
 * Given a base SKU and the current locale, compute the list of hreflang locale codes
 * where this PDP is activated in Algolia. This mirrors canonical logic by reusing
 * product activation as the source of truth.
 *
 * Returns array of strings like 'en-US', 'fr-FR'.
 */
export async function makeActivatedProductHrefLangs(
  sku: string,
  currentLocale: string,
  seoHrefLangConfig: { [language: string]: string[] },
  candidatesOverride?: string[],
  draftMode?: boolean,
): Promise<string[]> {
  const supportedCountries =
    LocaleConfiguration.getInstance().getSupportedCountries();
  const allCandidates =
    candidatesOverride ??
    makeHrefLangs(
      supportedCountries,
      seoHrefLangConfig,
      currentLocale?.split('-')[1],
    );

  const uniqueCandidates = Array.from(new Set(allCandidates));
  if (uniqueCandidates.length === 0) {
    return [];
  }

  // Build batched queries, one per locale/index
  const queries = uniqueCandidates.map((localeCode) => {
    const [language, country] = localeCode.split('-');
    // Construct router locale as country-language (lowercase), because index name selection is country-driven
    const routerLocale = `${country.toLowerCase()}-${language.toLowerCase()}`;
    const indexName = getIndexName('PRODUCT', routerLocale);
    const filters = applyProductConfigurationFilters(
      ProductConfigurationFilterType.PDP,
      undefined,
      false,
      `objectID:${sku}`,
    );
    return {
      indexName,
      query: '',
      params: {
        filters,
        queryType: 'prefixNone',
      },
    } as any;
  });

  const algoliaSsrSearchClient = createSearchClient({
    isSsr: true,
    isPreview: draftMode,
  });
  const response = await algoliaSsrSearchClient.multipleQueries(queries);

  const activatedLocales: string[] = [];
  response?.results?.forEach((result: any, idx: number) => {
    const hit = (result?.hits?.[0] ?? null) as
      | WatchHit
      | StrapHit
      | EyewearHit
      | null;
    const { isPdpActivated } = checkProductActivation(hit, false);
    if (hit && isPdpActivated) {
      activatedLocales.push(uniqueCandidates[idx]);
    }
  });

  return activatedLocales;
}

export const convertToOpeningHoursSpecification = (data: {
  [key: string]: {
    isClosed?: boolean;
    openIntervals?: {
      end: string;
      start: string;
    }[];
  };
}): OpeningHoursSpecification[] => {
  const specifications: OpeningHoursSpecification[] = [];

  const daysOfWeek = [
    'monday',
    'tuesday',
    'wednesday',
    'thursday',
    'friday',
    'saturday',
    'sunday',
  ];

  for (const day of daysOfWeek) {
    const dayData = data[day];

    if (dayData?.isClosed) {
      continue; // Skip closed days
    }

    if (dayData?.openIntervals) {
      for (const interval of dayData.openIntervals) {
        specifications.push({
          '@type': 'OpeningHoursSpecification',
          opens: interval.start,
          closes: interval.end,
          dayOfWeek: day.charAt(0).toUpperCase() + day.slice(1), // Capitalize the day
        });
      }
    }
  }

  return specifications;
};
